// @ts-nocheck
import Table from "common/Table/Table";
import ExpirationDatePicker from "features/admin/group/users/ExpirationDatePicker";
import ActionsDropdown from "features/admin/group/users/ActionsDropdown";
import Modal from "common/Modal/Modal";
import {
  NOT_IN_GROUP_STATUSES,
  USER_MEMBERSHIP_UNAUTHORIZED_STATUS,
} from "common/constants";
import ExclamationCircleOutlined from "@ant-design/icons/ExclamationCircleOutlined";
import { sortRawDates } from "@shared/utils/date";
import { find, union } from "lodash";
import { StatusWrapper } from "features/admin/group/users/styles";
import { STATUSES_INFO_MAP } from "features/admin/group/users/useFilters";
import { StatusCircle } from "Global/StatusCircle";
import React, { ReactNode, useState } from "react";
import Text from "common/Typography/Text";
import Popover from "common/Popover/Popover";
import Checkbox from "common/Checkbox/Checkbox";
import { ASSIGN_LICENSE_FAILURE_TYPE_TO_DISPLAY } from "features/admin/constants";
import { useSelector } from "react-redux";
import { selectSubGroupConfiguration } from "app/slices/admin.slice";
import { sortByStringProperty } from "@shared/utils/general";
import { openMessage } from "utils/misc";
import { ColumnsType } from "antd/es/table";
import { getTelegramUserLink } from "features/admin/group/utils";
import styled, { css, useTheme } from "styled-components";
import SortSelect from "common/Select/SortSelect";
import { phoneStackedRows } from "features/admin/NewLook/phoneRows";
import StatusDot from "features/admin/NewLook/StatusDot";
import { getMemberStatusColor } from "features/admin/NewLook/statusColors";
import PanelEmpty from "common/Empty/PanelEmpty.tsx";
import TableSkeleton from "features/admin/NewLook/TableSkeleton";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { useBottomNavigator } from "utils/hooks/misc";
import { SortingOrder } from "@shared/types";
import { Telegram } from "common/Icons/Icons";
import dayjs from "dayjs";
import {
  AssignLicensesFailureType,
  SubGroupUserMembership,
  LanguageNameSpaces,
} from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

export const sortByStatus = (a, b) => {
  const aStatusInfo = find(
    STATUSES_INFO_MAP,
    (statusesInfo) => statusesInfo.statuses.indexOf(a) > -1
  );
  const aSortValue = aStatusInfo ? aStatusInfo.sortValue : 0;
  const bStatusInfo = find(
    STATUSES_INFO_MAP,
    (statusesInfo) => statusesInfo.statuses.indexOf(b) > -1
  );
  const bSortValue = bStatusInfo ? bStatusInfo.sortValue : 0;
  if (aSortValue < bSortValue) return -1;
  if (bSortValue < aSortValue) return 1;
  return 0;
};

interface UsersTableProps {
  subGroupID: number;
  filteredUsers: SubGroupUserMembership[];
  selectedTelegramIds: number[];
  setSelectedTelegramIds: (telegramIds: number[]) => void;
  telegramIdToError?: Record<number, AssignLicensesFailureType>;
  setTelegramIdToError?: (
    telegramIdToError: Record<number, AssignLicensesFailureType>
  ) => void;
  isFetching: boolean;
  /** New look: why the list is empty when members exist (search/filters). */
  emptyReason?: "search" | "filters";
  /** New look (phones): controls shown next to the sort pill. */
  phoneToolbar?: ReactNode;
}

const UsersTable = ({
  subGroupID,
  filteredUsers,
  selectedTelegramIds,
  setSelectedTelegramIds,
  telegramIdToError,
  setTelegramIdToError,
  isFetching,
  emptyReason,
  phoneToolbar,
}: UsersTableProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const subGroupConfiguration = useSelector(
    selectSubGroupConfiguration(subGroupID)
  );
  const [saveUserExpiration, { isLoading: isUpdatingUserExpiration }] =
    sharedLib.sharedApi.admin.useSaveUserExpirationMutation();
  const [confirmModal, confirmModalContextHolder] = Modal.useModal();
  const [isTableLoading, setIsTableLoading] = useState(false);
  const theme = useTheme();
  const { isNewLook } = theme;
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  // New look (phones): rows stack into cards, so sorting moves to a sort pill.
  const isNewLookPhone = isNewLook && isBottomNavigator;
  const [phoneSort, setPhoneSort] = useState<{
    key: string;
    direction: SortingOrder;
  }>({ key: "status", direction: SortingOrder.DESCEND });

  const memberInGroup = (status) => {
    return NOT_IN_GROUP_STATUSES.indexOf(status) === -1;
  };

  const saveExpiration = (membershipId, date, successStr, successCallback) => {
    saveUserExpiration({
      sub_group_id: subGroupID,
      membership_id: membershipId,
      expiration_datetime: date,
    })
      .unwrap()
      .then(() => {
        openMessage(successStr || t("users.confirm.saveSuccess"), "success");
        successCallback && successCallback();
      })
      .catch((error) => {
        openMessage(
          error?.data?.message || t("users.confirm.saveError"),
          "error",
          8
        );
      });
  };

  const confirmWithCallback = (confirmStr, onOkCallback) => {
    confirmModal.confirm({
      title: t("users.confirm.title"),
      icon: <ExclamationCircleOutlined />,
      content: confirmStr,
      okText: t("users.confirm.save"),
      cancelText: t("users.confirm.cancel"),
      onOk: onOkCallback,
    });
  };

  const updateExpiration = (membership, date, successCallback) => {
    let confirmMessage = null;
    let dateIsBeforeNow = dayjs().isAfter(date);
    if (
      membership.status === USER_MEMBERSHIP_UNAUTHORIZED_STATUS &&
      !dateIsBeforeNow
    ) {
      confirmMessage = t(
        "users.confirm.confirm_unauthorized_switch_to_authorized"
      );
    } else if (!memberInGroup(membership.status) && !dateIsBeforeNow) {
      confirmMessage = t("users.confirm.confirm_not_in_group_unban");
    } else if (dateIsBeforeNow) {
      confirmMessage = t("users.confirm.confirm_date_less_than_now");
    }

    if (confirmMessage) {
      confirmWithCallback(confirmMessage, () =>
        saveExpiration(membership.id, date, null, successCallback)
      );
    } else {
      saveExpiration(membership.id, date, null, successCallback);
    }
  };

  const getStatusInfo = (status) =>
    find(
      STATUSES_INFO_MAP,
      (statusesInfo) => statusesInfo.statuses.indexOf(status) > -1
    );

  const renderStatus = (status) => {
    const displayInfo = getStatusInfo(status);
    if (isNewLook)
      return (
        <StatusDot
          color={getMemberStatusColor(theme, displayInfo?.displayName)}
        >
          {displayInfo ? t(displayInfo.displayName) : status}
        </StatusDot>
      );
    return (
      <StatusWrapper>
        <StatusCircle color={displayInfo.color} />
        <span>{t(displayInfo.displayName)}</span>
      </StatusWrapper>
    );
  };

  const expirationColumn = {
    title: t("users.table.groupExpiration"),
    dataIndex: "expiration_datetime",
    key: "expiration_datetime",
    showSorterTooltip: false,
    width: 200,
    render: (expiration, membership) => (
      <ExpirationDatePicker
        membership={membership}
        expiration={expiration}
        updateExpiration={updateExpiration}
        loading={isUpdatingUserExpiration}
      />
    ),
    sorter: (a, b) =>
      sortRawDates(a.expiration_datetime, b.expiration_datetime),
  };

  const actionColumn = {
    title: "",
    key: "action",
    width: isNewLook ? 56 : 60,
    render: (_, membership) => (
      <ActionsDropdown
        membership={membership}
        subGroupID={subGroupID}
        updateExpiration={updateExpiration}
        confirmWithCallback={confirmWithCallback}
        setTableIsLoading={setIsTableLoading}
      />
    ),
  };

  // New look: one name cell (the name over the Telegram handle) instead of
  // three text columns; the ID stays its own column on wider screens.
  const newLookColumns: ColumnsType<SubGroupUserMembership> = [
    {
      title: t("users.table.name"),
      dataIndex: "telegram_user",
      key: "name",
      showSorterTooltip: false,
      sorter: (a, b) =>
        sortByStringProperty(a, b, "telegram_user.first_name") ||
        sortByStringProperty(a, b, "telegram_user.last_name"),
      render: (telegramUser, membership) => {
        const name = [telegramUser?.first_name, telegramUser?.last_name]
          .filter(Boolean)
          .join(" ");
        return (
          <NameCell>
            <NameLine title={name}>{name || "—"}</NameLine>
            <NameMeta>
              {telegramUser?.username && (
                <NewLookTelegramLink
                  href={getTelegramUserLink(telegramUser)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(event) => event.stopPropagation()}
                >
                  @{telegramUser.username}
                </NewLookTelegramLink>
              )}
              {isNewLookPhone && (
                <span>
                  {telegramUser?.username && " · "}
                  {membership.telegram_user_id}
                </span>
              )}
            </NameMeta>
          </NameCell>
        );
      },
    },
    {
      title: t("users.table.telegramId"),
      dataIndex: "telegram_user_id",
      key: "telegram_user_id",
      width: 150,
      render: (telegramUserId) => (
        <SecondaryFigure>{telegramUserId}</SecondaryFigure>
      ),
    },
    expirationColumn,
    {
      title: t("users.table.status"),
      dataIndex: "status",
      key: "status",
      width: 190,
      defaultSortOrder: "descend",
      render: (status) => renderStatus(status),
      sorter: (a, b) => sortByStatus(a.status, b.status),
    },
    actionColumn,
  ];

  const classicColumns: ColumnsType<SubGroupUserMembership> = [
    {
      title: t("users.table.firstName"),
      dataIndex: "telegram_user",
      key: "first_name",
      width: 150,
      showSorterTooltip: false,
      sorter: (a, b) => sortByStringProperty(a, b, "telegram_user.first_name"),
      render: (telegramUser, _) =>
        telegramUser && (
          <StyledText $ellipsis={true}>{telegramUser.first_name}</StyledText>
        ),
    },
    {
      title: t("users.table.lastName"),
      dataIndex: "telegram_user",
      key: "last_name",
      width: 150,
      showSorterTooltip: false,
      sorter: (a, b) => sortByStringProperty(a, b, "telegram_user.last_name"),
      render: (telegramUser, _) =>
        telegramUser && (
          <StyledText $ellipsis={true}>{telegramUser.last_name}</StyledText>
        ),
    },
    {
      title: t("users.table.username"),
      dataIndex: "telegram_user",
      key: "username",
      showSorterTooltip: false,
      sorter: (a, b) => sortByStringProperty(a, b, "telegram_user.username"),
      render: (telegramUser, _) =>
        telegramUser?.username && (
          <TelegramUserLink
            href={getTelegramUserLink(telegramUser)}
            target="_blank"
          >
            {telegramUser.username}
            <Telegram style={{ marginLeft: 8, width: 18, height: 18 }} />
          </TelegramUserLink>
        ),
    },
    {
      title: t("users.table.telegramId"),
      dataIndex: "telegram_user_id",
      key: "telegram_user_id",
    },
    {
      title: t("users.table.groupExpiration"),
      dataIndex: "expiration_datetime",
      key: "expiration_datetime",
      showSorterTooltip: false,
      width: 200,
      render: (expiration, membership) => (
        <ExpirationDatePicker
          membership={membership}
          expiration={expiration}
          updateExpiration={updateExpiration}
          loading={isUpdatingUserExpiration}
        />
      ),
      sorter: (a, b) =>
        sortRawDates(a.expiration_datetime, b.expiration_datetime),
    },
    {
      title: t("users.table.status"),
      dataIndex: "status",
      key: "status",
      defaultSortOrder: "descend",
      render: (status, _) => {
        const displayInfo = find(
          STATUSES_INFO_MAP,
          (statusesInfo) => statusesInfo.statuses.indexOf(status) > -1
        );
        return (
          <StatusWrapper>
            <StatusCircle color={displayInfo.color} />
            <span>{t(displayInfo.displayName)}</span>
          </StatusWrapper>
        );
      },
      sorter: (a, b) => sortByStatus(a.status, b.status),
    },
    {
      title: "",
      key: "action",
      width: 60,
      render: (_, membership) => (
        <ActionsDropdown
          membership={membership}
          subGroupID={subGroupID}
          updateExpiration={updateExpiration}
          confirmWithCallback={confirmWithCallback}
          setTableIsLoading={setIsTableLoading}
        />
      ),
    },
  ];

  const columns = isNewLook ? newLookColumns : classicColumns;

  if (subGroupConfiguration?.allow_license) {
    columns.splice(isNewLook ? 3 : 5, 0, {
      title: t("users.table.licenseStatus"),
      key: "licenseStatus",
      showSorterTooltip: false,
      width: 150,
      render: (_, membership) =>
        isNewLook ? (
          membership?.user?.license_status ? (
            t(membership.user.license_status)
          ) : (
            <Muted>—</Muted>
          )
        ) : (
          membership?.user?.license_status
        ),
      sorter: (a, b) => sortByStringProperty(a, b, "user.license_status"),
    });
  }

  if (isNewLookPhone) {
    columns.forEach((column) => {
      const key = column.key as string;
      column.className = `nl-${key}`;
      if (column.sorter) {
        column.sortOrder =
          phoneSort.key === key
            ? phoneSort.direction === SortingOrder.ASCEND
              ? "ascend"
              : "descend"
            : null;
      }
      if (
        key === "expiration_datetime" ||
        key === "licenseStatus" ||
        (isNewLook && key === "status")
      ) {
        column.onCell = () => ({ "data-label": column.title });
      }
    });
  }
  const phoneSortValues = columns
    .filter((column) => column.sorter)
    .map((column) => column.key as string);
  const phoneSortLabel = (key: string) =>
    (columns.find((column) => column.key === key)?.title as string) ?? key;

  const isAllMembershipsSelected =
    filteredUsers.length &&
    filteredUsers.filter(
      (membership) => !selectedTelegramIds.includes(membership.telegram_user_id)
    ).length === 0;

  const handleUnselectMemberships = (
    memberships: SubGroupUserMembership[]
  ): SubGroupUserMembership[] => {
    const telegramIdsToUnselect = memberships.map(
      (membership) => membership.telegram_user_id
    );
    const updatedSelectedTelegramIds = selectedTelegramIds.filter(
      (telegramId) => !telegramIdsToUnselect.includes(telegramId)
    );

    const updatedErrorTelegramIds = Object.keys(telegramIdToError || {}).filter(
      (telegramId) => updatedSelectedTelegramIds.includes(telegramId)
    );
    let updatedTelegramIdToError;
    if (updatedErrorTelegramIds.length) {
      updatedTelegramIdToError = updatedErrorTelegramIds.reduce(
        (acc, telegramId) => {
          acc[telegramId] = telegramIdToError[telegramId];
          return acc;
        },
        {}
      );
    }
    setTelegramIdToError(updatedTelegramIdToError);
    return updatedSelectedTelegramIds;
  };

  const handleSelectMemberships = (
    memberships: SubGroupUserMembership[]
  ): number[] => {
    return union(
      selectedTelegramIds,
      memberships.map((userData) => userData.telegram_user_id)
    );
  };

  const onChangeSelectedMemberships = (
    memberships: SubGroupUserMembership[],
    isSelected?: boolean
  ) => {
    let updatedSelectedTelegramIds: number[];
    if (isSelected ?? !isAllMembershipsSelected) {
      updatedSelectedTelegramIds = handleSelectMemberships(memberships);
    } else {
      updatedSelectedTelegramIds = handleUnselectMemberships(memberships);
    }
    setSelectedTelegramIds(updatedSelectedTelegramIds);
  };

  const onSelectMembership = (
    membership: SubGroupUserMembership,
    isSelected: boolean
  ) => {
    onChangeSelectedMemberships([membership], isSelected);
  };

  const getMembershipCheckbox = (
    membership: SubGroupUserMembership,
    isChecked: boolean
  ) => {
    const errorKey =
      ASSIGN_LICENSE_FAILURE_TYPE_TO_DISPLAY[
        telegramIdToError?.[membership.telegram_user_id]
      ];
    const errorDisplay = errorKey ? t(errorKey) : null;
    let checkbox = (
      <Checkbox
        checked={isChecked}
        onChange={(e) => onSelectMembership(membership, e.target.checked)}
        $isError={!!errorDisplay}
      />
    );
    if (errorDisplay) {
      checkbox = (
        <Popover content={errorDisplay} placement="topLeft">
          {checkbox}
        </Popover>
      );
    }
    return checkbox;
  };

  const newLookEmpty = (
    <PanelEmpty
      title={
        emptyReason === "search"
          ? tCommon("empty.no_results_matching_search", {
              objectName: t("users.table.itemsType"),
            })
          : emptyReason === "filters"
          ? tCommon("empty.no_results_matching_filters", {
              objectName: t("users.table.itemsType"),
            })
          : t("users.empty.title")
      }
      description={emptyReason ? undefined : t("users.empty.description")}
    />
  );

  // New look: rows shaped like members while the first load runs, instead
  // of a spinner over the empty state.
  const showSkeleton = isNewLook && isFetching && !filteredUsers.length;

  const table = (
    <Table<SubGroupUserMembership>
      columns={columns}
      dataSource={filteredUsers}
      loading={
        !showSkeleton &&
        (isTableLoading || isUpdatingUserExpiration || isFetching)
      }
      locale={
        isNewLook
          ? { emptyText: showSkeleton ? <TableSkeleton /> : newLookEmpty }
          : undefined
      }
      rowSelection={
        subGroupConfiguration?.allow_license
          ? {
              columnWidth: isNewLook ? 52 : 50,
              selectedRowKeys: selectedTelegramIds,
              onSelect: (membership, isSelected) =>
                onSelectMembership(membership, isSelected),
              onSelectAll: () => onChangeSelectedMemberships(filteredUsers),
              renderCell: (checked, membership) =>
                getMembershipCheckbox(membership, checked),
              columnTitle: (
                <Checkbox
                  checked={isAllMembershipsSelected}
                  indeterminate={
                    !isAllMembershipsSelected && selectedTelegramIds.length
                  }
                  onChange={() => onChangeSelectedMemberships(filteredUsers)}
                />
              ),
            }
          : undefined
      }
      itemsType={t("users.table.itemsType")}
      onChange={
        isNewLookPhone
          ? (_pagination, _filters, sorter) => {
              const { columnKey, order } = Array.isArray(sorter)
                ? sorter[0]
                : sorter;
              if (columnKey && order)
                setPhoneSort({
                  key: columnKey as string,
                  direction:
                    order === "ascend"
                      ? SortingOrder.ASCEND
                      : SortingOrder.DESCEND,
                });
            }
          : undefined
      }
    />
  );

  if (isNewLookPhone) {
    return (
      <>
        <PhoneSortRow $inPanel={!!phoneToolbar}>
          {phoneToolbar}
          <SortSelect
            sortBy={phoneSort.key}
            setSortBy={(key: string) =>
              setPhoneSort({ key, direction: SortingOrder.DESCEND })
            }
            sortDirection={phoneSort.direction}
            setSortDirection={(direction: SortingOrder) =>
              setPhoneSort((sort) => ({ ...sort, direction }))
            }
            sortValues={phoneSortValues}
            labelMapper={phoneSortLabel}
          />
        </PhoneSortRow>
        <PhoneRows $hasSelection={!!subGroupConfiguration?.allow_license}>
          {table}
        </PhoneRows>
        {confirmModalContextHolder}
      </>
    );
  }

  return (
    <>
      {table}
      {confirmModalContextHolder}
    </>
  );
};

export default UsersTable;

const PhoneSortRow = styled.div<{ $inPanel?: boolean }>`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
  ${(p) =>
    p.$inPanel &&
    css`
      align-items: center;
      gap: 8px;
      margin: 0;
      padding: 12px 16px;
      border-bottom: 1px solid ${getNewLookTokens(p.theme).hairline};
      > .ant-input-affix-wrapper,
      > span:first-child {
        flex: 1 1 auto;
        min-width: 0;
      }
    `}
`;
/**
 * New look (phones): each member is a stacked row - the name over the
 * Telegram handle and ID with the menu beside it, then the status,
 * expiration and license as labelled lines - instead of a table that
 * scrolls sideways.
 */
const PhoneRows = styled.div<{ $hasSelection: boolean }>`
  ${phoneStackedRows};
  .ant-table-tbody > tr.ant-table-row {
    grid-template-columns:
      ${(p) => (p.$hasSelection ? "28px" : "0")} minmax(0, 1fr)
      32px;
    grid-template-areas:
      "sel name action"
      "sel status status"
      "sel exp exp"
      "sel license license";
    padding: 12px 8px 12px 16px;
    border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  .ant-table-tbody > tr.ant-table-row:last-child {
    border-bottom: 0;
  }
  td.ant-table-selection-column {
    grid-area: sel;
    align-self: start;
    padding-top: 2px !important;
  }
  td.nl-name {
    grid-area: name;
    padding-bottom: 4px !important;
  }
  td.nl-telegram_user_id {
    display: none !important;
  }
  td.nl-status {
    grid-area: status;
  }
  td.nl-action {
    grid-area: action;
    align-self: start;
    margin-top: -6px;
    text-align: right;
  }
  td.nl-expiration_datetime {
    grid-area: exp;
  }
  td.nl-licenseStatus {
    grid-area: license;
  }
  td[data-label] {
    min-height: 32px;
    padding-right: 8px !important;
    color: ${(p) => getNewLookTokens(p.theme).text};
  }
  td[data-label]::before {
    ${newLookType.label};
    color: ${(p) => getNewLookTokens(p.theme).secondary};
  }
  ${(p) =>
    !p.$hasSelection &&
    css`
      .ant-table-tbody > tr.ant-table-row {
        column-gap: 0;
      }
      td.nl-name {
        padding-right: 8px !important;
      }
    `}
`;
const NameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const NameLine = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const NameMeta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &:empty {
    display: none;
  }
`;
const NewLookTelegramLink = styled.a`
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  transition: color 120ms;
  &:hover {
    color: ${(p) => getNewLookTokens(p.theme).accent};
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 1px;
    border-radius: 4px;
  }
`;
const SecondaryFigure = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  font-size: 13.5px;
  font-variant-numeric: tabular-nums;
`;
const Muted = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const TelegramUserLink = styled.a`
  color: ${(p) => p.theme.colors.textColor};
  display: flex;
  align-items: center;
  &:hover {
    color: ${(p) => p.theme.colors.primaryBlue};
    fill: ${(p) => p.theme.colors.primaryBlue};
  }
`;
const StyledText = styled(Text)`
  color: ${(p) => p.theme.colors.textColor};
`;
